import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import { Tooltip } from '@shared/components/ui/tooltip';
import {
  faBagShopping,
  faCheck,
  faChevronDown,
  faCircleInfo,
  faEye,
  faNoteSticky,
  faShoppingBag,
  faXmark,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCanManageCRM } from '@modules/CRM/shared/hooks/useCRMSettings';
import { formatCurrencyAmount, useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { CustomColDef } from '@shared/components/ui/AgGridTable';
import { ColumnDef } from '@tanstack/react-table';
import { ColDef } from 'ag-grid-community';
import { useCallback, useMemo, useRef, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { DropdownActions } from '../components/interaction/DropdownActions';
import { ProductsModalRef } from '../components/modals';
import { getStatusColor } from '../config/statusConfig';
import { CRMModel, CrmStatusModel } from '../models/CRM';
import { getTextColorForBackground } from '../utils/colorUtils';
import { Filters } from '../models/Filters';
import { useCountries } from '@shared/hooks/useCountries';

interface ActionItem {
  id: string;
  label: string;
  enabled: boolean;
  icon: any;
  onClick: () => void;
}

interface UseCRMAgGridColumnsProps {
  selectedColumns?: string[];
  refetch?: () => void;
  onViewClick?: (order: CRMModel, e?: MouseEvent) => void;
  onNotesClick?: (order: CRMModel) => void;
  onTagsClick?: (order: CRMModel) => void;
  onTagRemove?: (orderId: number, tagId: number) => void;
  statusList?: CrmStatusModel[];
  onStatusChange?: (orderId: number, crmStatusId: number | null) => void;
  onCustomerOrdersClick?: (customerId: number, customerName: string) => void;
  filters?: Filters;
  getAvailableActions?: (order: CRMModel) => ActionItem[];
}

/** Espacio reservado para el botón "+" y el gap (px) */
const TAGS_BUTTON_RESERVED_PX = 42;
/** Gap entre etiquetas (gap-1.5 = 6px) */
const GAP_BETWEEN_TAGS_PX = 6;
/** Estimación de ancho de un tag: base (padding + icono + gaps) + ~7px por carácter del nombre */
function estimateTagWidthPx(name: string): number {
  const basePx = 40;
  const pxPerChar = 7;
  return basePx + pxPerChar * (name?.length ?? 0);
}
/** Calcula cuántas etiquetas caben en el ancho disponible y cuántas quedan ocultas */
function getVisibleAndOverflowCount(
  tags: { name: string }[],
  columnWidthPx: number,
): { visibleCount: number; overflowCount: number } {
  const availablePx = Math.max(0, columnWidthPx - TAGS_BUTTON_RESERVED_PX);
  let usedPx = 0;
  let visibleCount = 0;
  for (const tag of tags) {
    if (visibleCount > 0) usedPx += GAP_BETWEEN_TAGS_PX;
    usedPx += estimateTagWidthPx(tag.name);
    if (usedPx <= availablePx) visibleCount++;
    else break;
  }
  const overflowCount = tags.length - visibleCount;
  return { visibleCount, overflowCount };
}

const formatDateTime = (date?: string | null) => {
  if (!date) {
    return '-';
  }

  try {
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) {
      return '-';
    }

    const day = parsed.getDate().toString().padStart(2, '0');
    const month = (parsed.getMonth() + 1).toString().padStart(2, '0');
    const year = parsed.getFullYear();
    const hours = parsed.getHours().toString().padStart(2, '0');
    const minutes = parsed.getMinutes().toString().padStart(2, '0');

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch {
    return '-';
  }
};

const convertToTanStackColumns = (agGridColumns: ColDef[]): ColumnDef<CRMModel>[] =>
  agGridColumns
    .filter((column) => {
      const field = column.field || column.headerName || 'unknown';
      return field !== 'actions' && field !== 'checkbox' && field !== 'unknown';
    })
    .map((column) => ({
      id: column.field || column.headerName || 'unknown',
      accessorKey: column.field || column.headerName || 'unknown',
      header: column.headerName || 'Columna',
    }));

export const useCRMAgGridColumns = ({
  selectedColumns = [],
  onViewClick,
  onNotesClick,
  onTagsClick,
  onTagRemove,
  statusList = [],
  onStatusChange,
  onCustomerOrdersClick,
  getAvailableActions,
}: UseCRMAgGridColumnsProps) => {
  const { t } = useTranslation('crmModule');
  const defaultCurrency = useCurrency();
  const productsModalRef = useRef<ProductsModalRef>(null);
  const { canManage } = useCanManageCRM();
  const { countries } = useCountries();

  const countryNameByIso2 = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of countries) {
      if (c.code) map.set(c.code, c.name);
    }
    return map;
  }, [countries]);

  // Handler para abrir el modal de productos
  const handleOpenProductsModal = useCallback((order: CRMModel) => {
    const showNotAssigned = order.status_id === 1 || order.status_id === 2;
    const lines = order.lines || [];
    const totalAmount = typeof order.total === 'string' ? parseFloat(order.total) : order.total || 0;

    const payload = {
      orderNumber: order.ref,
      stockMessage:
        'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
      currency: order.currency, // Pasar el currency de la orden
      products: lines.map((line) => {
        // Convertir amount y qty a números para evitar errores con toFixed
        const amount = typeof line.amount === 'string' ? parseFloat(line.amount) : line.amount || 0;
        const qty = typeof line.qty === 'string' ? parseFloat(line.qty) : line.qty || 0;
        return {
          name: line.name || line.product?.name || '',
          image: line.product?.image_hash || '',
          badge: line.product?.code || '',
          qty: qty,
          price: amount,
          subtotal: qty * amount,
        };
      }),
      subtotal: lines.reduce((acc, line) => {
        const amount = typeof line.amount === 'string' ? parseFloat(line.amount) : line.amount || 0;
        const qty = typeof line.qty === 'string' ? parseFloat(line.qty) : line.qty || 0;
        return acc + qty * amount;
      }, 0),
      shipping: typeof order.total_shipping === 'string' ? parseFloat(order.total_shipping) : order.total_shipping || 0,
      discount: 0, // No hay campo discount en el modelo actual
      total: totalAmount,
      showNotAssignedAlert: showNotAssigned,
    };
    productsModalRef.current?.openWithData(payload);
  }, []);

  const allColumnDefs: ColDef[] = useMemo(() => {
    const baseColumns: CustomColDef[] = [
      {
        field: 'ref',
        headerName: t('table.order'),
        sortable: true,
        minWidth: 150,
        cellRenderer: (params: any) => {
          const order = params.data as CRMModel;
          return <span className="text-sm font-medium text-text">{order.ref}</span>;
        },
      },
      {
        field: 'status',
        headerName: t('table.status'),
        sortable: false,
        minWidth: 180,
        cellDataType: false,
        valueGetter: (params) => params.data.status_id,
        cellRenderer: (params: any) => {
          const order = params.data as CRMModel;
          // Manejar tanto array como objeto para crm_status
          const crmStatus = order.crm_status;
          const isArray = Array.isArray(crmStatus);
          const hasCrmStatus = isArray ? crmStatus?.length > 0 : !!crmStatus;
          const currentStatus = isArray ? crmStatus?.[0] : crmStatus;
          const currentStatusId = currentStatus?.id ?? null;
          // Verificar si el estado actual es fijo (sin team_id)
          const isFixedStatus = currentStatus && currentStatus.team_id == null;

          const triggerBadge = !hasCrmStatus ? (
            <Badge className="text-xs bg-bg-error text-error rounded-full px-3 py-1 inline-flex items-center gap-1.5">
              {t('table.noStatus', 'Sin estado')}
              {!isFixedStatus && <FontAwesomeIcon icon={faChevronDown} className="w-3 h-3 opacity-70" />}
            </Badge>
          ) : (
            <Badge
              className={`text-xs rounded-full px-3 py-1 inline-flex items-center gap-1.5 ${!currentStatus?.color ? getStatusColor(order.status_id) : ''}`}
              style={
                currentStatus?.color
                  ? {
                      backgroundColor: currentStatus.color,
                      color: getTextColorForBackground(currentStatus.color),
                    }
                  : undefined
              }
            >
              {currentStatus?.name ?? `Estado ${order.status_id}`}
              {!isFixedStatus && <FontAwesomeIcon icon={faChevronDown} className="w-3 h-3 opacity-80" />}
            </Badge>
          );

          // Información del gestor y fecha
          const managedBy = currentStatus?.managed_by;
          const managedAt = currentStatus?.managed_at;
          const hasManagementInfo = managedBy || managedAt;

          // Construir contenido del tooltip
          const tooltipContent = hasManagementInfo ? (
            <div className="space-y-1 text-xs">
              {managedBy && (
                <div>
                  <span className="font-medium">{t('table.managedBy', 'Gestionado por')}:</span> {managedBy.name}
                </div>
              )}
              {managedAt && (
                <div>
                  <span className="font-medium">{t('table.managedAt', 'Fecha')}:</span> {formatDateTime(managedAt)}
                </div>
              )}
            </div>
          ) : null;

          const statusContent = (
            <div className="flex items-center gap-2">
              {triggerBadge}
              {tooltipContent && (
                <Tooltip
                  content={
                    <div className="bg-card-bg border border-card-border rounded-lg p-2 max-w-xs">{tooltipContent}</div>
                  }
                  trigger={
                    <FontAwesomeIcon icon={faCircleInfo} className="w-3 h-3 text-complementary-text cursor-help" />
                  }
                />
              )}
            </div>
          );

          // Si el estado es fijo, no puede gestionar CRM, o no hay función de cambio, mostrar solo el badge
          if (isFixedStatus || !canManage || !onStatusChange || statusList.length === 0) {
            return statusContent;
          }

          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="cursor-pointer hover:opacity-90 transition-opacity text-left w-full"
                  data-testid={`status-dropdown-${order.id}`}
                >
                  {statusContent}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="max-h-[280px] overflow-y-auto">
                {statusList
                  .sort((a, b) => {
                    // Estados editables (con team_id) primero, estados fijos (sin team_id) al final
                    const aHasTeamId = a.team_id != null;
                    const bHasTeamId = b.team_id != null;
                    if (aHasTeamId && !bHasTeamId) return -1;
                    if (!aHasTeamId && bHasTeamId) return 1;
                    return 0;
                  })
                  .map((status) => {
                    const isEditable = status.team_id != null;
                    const isSelected = currentStatusId === status.id;
                    return (
                      <DropdownMenuItem
                        key={status.id}
                        onClick={() => isEditable && onStatusChange(order.id, status.id)}
                        disabled={!isEditable}
                        data-testid={`status-option-${status.id}`}
                      >
                        <span className="inline-flex items-center gap-2 w-full">
                          {isSelected ? (
                            <FontAwesomeIcon icon={faCheck} className="w-3.5 h-3.5 text-primary shrink-0" />
                          ) : (
                            <span className="w-3.5 shrink-0" />
                          )}
                          <span
                            className={`rounded-full px-2 py-0.5 text-xs min-w-[4rem] text-center ${!status.color ? 'bg-card-border text-text' : ''}`}
                            style={
                              status.color
                                ? {
                                    backgroundColor: status.color,
                                    color: getTextColorForBackground(status.color),
                                  }
                                : undefined
                            }
                          >
                            {status.name}
                          </span>
                        </span>
                      </DropdownMenuItem>
                    );
                  })}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
      {
        field: 'client_name',
        headerName: t('table.client'),
        sortable: false,
        minWidth: 200,
        cellRenderer: (params: any) => {
          const order = params.data as CRMModel;
          return <span className="text-sm text-text">{order.client_name}</span>;
        },
      },
      {
        field: 'shop_name',
        headerName: t('table.shop', 'Tienda'),
        sortable: false,
        minWidth: 150,
        cellRenderer: (params: any) => {
          const order = params.data as CRMModel;
          return <span className="text-sm text-text">{order.shop_name || '-'}</span>;
        },
      },
      {
        field: 'shipping',
        headerName: t('table.recipient', 'Destinatario'),
        sortable: false,
        minWidth: 200,
        cellDataType: false,
        cellRenderer: (params: any) => {
          const order = params.data as CRMModel;
          const recipientName = order.shipping?.name;
          return <span className="text-sm text-text">{recipientName || '-'}</span>;
        },
      },

      // 🔵 COLUMNA: País de destino (country_code)
      {
        field: 'shipping_country_code',
        headerName: t('table.destinationCountry', 'País de destino'),
        sortable: false,
        minWidth: 140,
        cellRenderer: (params: any) => {
          const order = params.data as CRMModel;
          const destinationCountryCode = order.shipping?.address?.country_code;
          const destinationCountryName = destinationCountryCode
            ? countryNameByIso2.get(destinationCountryCode)
            : undefined;

          const destinationText = destinationCountryName
            ? `${destinationCountryName} (${destinationCountryCode})`
            : destinationCountryCode || '-';
          return <span className="text-sm text-text">{destinationText}</span>;
        },
      },
      {
        field: 'created_at',
        headerName: t('table.created_at'),
        sortable: true,
        minWidth: 160,
        valueGetter: (params) => params.data.created_at,
        cellRenderer: (params: any) => {
          const order = params.data as CRMModel;
          return <span className="text-sm text-text">{formatDateTime(order.created_at)}</span>;
        },
      },
      {
        field: 'products',
        headerName: t('table.products', 'Productos'),
        sortable: false,
        minWidth: 100,
        cellRenderer: (params: any) => {
          const order = params.data as CRMModel;
          const lines = order.lines || [];
          const totalProducts = lines.reduce((acc, line) => acc + (line.qty || 0), 0);
          const isRed = order.status_id === 1 || order.status_id === 2;
          const color = isRed ? 'bg-error' : 'bg-success';

          return (
            <span className="text-sm font-medium flex items-center gap-2 relative">
              <button
                type="button"
                className="relative inline-flex items-center gap-2 focus:outline-none hover:opacity-70"
                onClick={() => handleOpenProductsModal(order)}
                title={t('table.viewProducts', 'Ver productos')}
              >
                <span className="relative inline-block">
                  <FontAwesomeIcon icon={faBagShopping} className="w-3 h-3" />
                  <span
                    className={`absolute -top-1 -right-0 w-1.5 h-1.5 mt-2.5 rounded-full border border-black ${color}`}
                  ></span>
                </span>
                {totalProducts}
              </button>
            </span>
          );
        },
      },
      {
        field: 'total',
        headerName: t('table.total'),
        sortable: true,
        minWidth: 120,
        valueGetter: (params) => {
          const total = params.data.total;
          return typeof total === 'string' ? parseFloat(total) : total;
        },
        cellRenderer: (params: any) => {
          const order = params.data as CRMModel;
          // Usar currency del pedido si está disponible, sino usar la moneda por defecto del usuario
          const currency = order.currency || defaultCurrency;
          const totalAmount = typeof order.total === 'string' ? parseFloat(order.total) : order.total;
          return <span className="text-sm font-medium text-text">{formatCurrencyAmount(totalAmount, currency)}</span>;
        },
      },
      {
        field: 'tags',
        headerName: t('table.tags'),
        sortable: false,
        minWidth: 240,
        cellDataType: false,
        cellRenderer: (params: any) => {
          const order = params.data as CRMModel;
          const tags = order.tags || [];
          const columnWidthPx = (params.column?.getActualWidth?.() as number | undefined) ?? 240;
          const { visibleCount, overflowCount } = getVisibleAndOverflowCount(tags, columnWidthPx);
          const visibleTags = tags.slice(0, visibleCount);

          return (
            <div className="flex items-center gap-1.5 min-w-0 w-full max-w-full">
              {/* Contenedor de etiquetas: solo ocupa el ancho de las etiquetas; el botón va justo después */}
              <div className="flex items-center gap-1.5 min-w-0 flex-nowrap overflow-hidden max-w-full">
                {visibleTags.map((tag) => (
                  <span
                    key={tag.id}
                    className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs shrink-0"
                    style={
                      tag.color
                        ? {
                            backgroundColor: tag.color,
                            color: getTextColorForBackground(tag.color),
                          }
                        : { backgroundColor: 'var(--wrh-card-border)', color: 'var(--wrh-text)' }
                    }
                  >
                    <span>{tag.name}</span>
                    {onTagRemove && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onTagRemove(order.id, tag.id);
                        }}
                        disabled={!canManage}
                        className="flex items-center justify-center w-4 h-4 rounded-full hover:bg-hover-press transition-colors ml-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid={`remove-tag-${order.id}-${tag.id}`}
                        aria-label={t('tags.remove', 'Eliminar etiqueta')}
                      >
                        <FontAwesomeIcon
                          icon={faXmark}
                          className="w-2.5 h-2.5"
                          style={{ color: tag.color ? getTextColorForBackground(tag.color) : 'var(--wrh-text)' }}
                        />
                      </button>
                    )}
                  </span>
                ))}
              </div>
              {/* Botón a continuación de las etiquetas: muestra +N si hay etiquetas ocultas por ancho */}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onTagsClick?.(order)}
                disabled={!canManage}
                className="shrink-0 h-6 min-w-[2.25rem] px-1.5 text-xs text-complementary-text hover:text-text rounded-md"
                data-testid={`tags-button-${order.id}`}
              >
                {overflowCount > 0 ? `+${overflowCount}` : '+'}
              </Button>
            </div>
          );
        },
      },
      {
        field: 'notes',
        headerName: t('table.notes'),
        sortable: false,
        minWidth: 100,
        cellDataType: false,
        cellRenderer: (params: any) => {
          const order = params.data as CRMModel;
          const notesCount = order.notes?.length || 0;
          return (
            <button
              type="button"
              onClick={() => onNotesClick?.(order)}
              disabled={!canManage}
              className="flex items-center gap-2 hover:opacity-70 transition-opacity cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid={`notes-button-${order.id}`}
            >
              <FontAwesomeIcon icon={faNoteSticky} className="w-3.5 h-3.5 text-icons" />
              <span className="text-sm text-text">{notesCount}</span>
            </button>
          );
        },
      },
      {
        field: 'total_orders_by_customer',
        headerName: t('table.totalOrdersByCustomer', 'Pedidos del cliente'),
        sortable: false,
        minWidth: 150,
        cellRenderer: (params: any) => {
          const order = params.data as CRMModel;
          const totalOrders = order.total_orders_by_customer ?? 0;
          const customerId = order.customer_id;
          const recipientName = order.shipping?.name || order.client_name || '';

          if (!customerId || totalOrders === 0) {
            return <span className="text-sm text-complementary-text">{totalOrders}</span>;
          }

          return (
            <button
              type="button"
              onClick={() => onCustomerOrdersClick?.(customerId, recipientName)}
              className="flex items-center gap-2 hover:opacity-70 transition-opacity cursor-pointer text-text hover:underline"
              data-testid={`customer-orders-button-${order.id}`}
            >
              <FontAwesomeIcon icon={faShoppingBag} className="w-3.5 h-3.5" />
              <span className="text-sm font-medium">{totalOrders}</span>
            </button>
          );
        },
      },
    ];

    baseColumns.push({
      field: 'actions',
      headerName: '',
      width: 100,
      minWidth: 100,
      maxWidth: 100,
      resizable: false,
      pinned: 'right',
      lockPosition: 'right',
      suppressMovable: true,
      suppressSizeToFit: true,
      suppressAutoSize: true,
      cellRenderer: (params: any) => {
        const order = params.data as CRMModel;
        return (
          <div className="flex items-center justify-end gap-2 w-full">
            {/* Icono de ver */}
            <button
              type="button"
              onClick={(e) => onViewClick?.(order, e)}
              className="flex items-center justify-center w-8 h-8 rounded-md hover:bg-hover-press transition-colors"
              data-testid={`view-order-${order.id}`}
            >
              <FontAwesomeIcon icon={faEye} className="w-4 h-4 text-icons" />
            </button>
            {/* Dropdown de acciones */}
            <DropdownActions order={order} actions={getAvailableActions?.(order)} />
          </div>
        );
      },
    });

    return baseColumns;
  }, [
    t,
    onViewClick,
    onNotesClick,
    onTagsClick,
    onTagRemove,
    statusList,
    onStatusChange,
    handleOpenProductsModal,
    canManage,
    onCustomerOrdersClick,
    getAvailableActions,
    defaultCurrency,
    countryNameByIso2,
  ]);

  const filteredColumnDefs = useMemo(() => {
    const alwaysVisible = allColumnDefs.filter((column) => {
      const field = column.field || column.headerName || 'unknown';
      return field === 'actions' || field === 'ref' || field === 'status';
    });

    const selectableColumns = allColumnDefs.filter((column) => {
      const field = column.field || column.headerName || 'unknown';
      return field !== 'actions' && field !== 'checkbox';
    });

    if (!selectedColumns.length) {
      return [...selectableColumns, ...alwaysVisible].reduce<ColDef[]>((acc, column) => {
        const field = column.field || column.headerName || 'unknown';
        if (!acc.some((existing) => (existing.field || existing.headerName || 'unknown') === field)) {
          acc.push(column);
        }
        return acc;
      }, []);
    }

    const selectedDefs = selectableColumns.filter((column) => {
      const field = column.field || column.headerName || 'unknown';
      return selectedColumns.includes(field);
    });

    const combined = [...selectedDefs];
    alwaysVisible.forEach((column) => {
      const field = column.field || column.headerName || 'unknown';
      if (!combined.some((existing) => (existing.field || existing.headerName || 'unknown') === field)) {
        combined.push(column);
      }
    });

    return combined;
  }, [allColumnDefs, selectedColumns]);

  const tanStackColumns = useMemo(() => convertToTanStackColumns(allColumnDefs), [allColumnDefs]);

  return {
    allColumnDefs,
    filteredColumnDefs,
    tanStackColumns,
    productsModalRef,
  };
};
